import React, { KeyboardEvent } from 'react';
import { FieldCompatibleProps } from '../Field';
import { HelpText } from '../HelpText';
import { Intent, useFullTheme } from '~/design';
import { useId } from '~/utils';
import { InternalTextInput, LabelText } from '~/internal';

export interface NumberInputProps extends FieldCompatibleProps<string, HTMLInputElement> {
  /** The aria-decsribed-by of the number field. */
  'aria-describedby'?: string;

  /** The aria-label of the number field. */
  'aria-label'?: string;

  /** If true, the number field cannot be interacted with. */
  disabled?: boolean;

  /** The intent of the number field. Maps to color. */
  intent?: Intent;

  /** The maximum value of the number field. This only applies when the user interacts with the input using the up/down arrows,
   * so a user could still type in a value larger than this. */
  max?: number;

  /** The minimum value of the number field. This only applies when the user interacts with the input using the up/down arrows,
   * so a user could still type in a value smaller than this. */
  min?: number;

  /** A callback function that is invoked when a key is pressed. */
  onKeyDown?: (key: React.KeyboardEvent<HTMLInputElement>['key']) => void;

  /** The placeholder of the number field. Placeholders are considered bad for accessibility, so it's *strongly encouraged* to avoid using this.
   * See this article for more details: https://www.smashingmagazine.com/2018/06/placeholder-attribute/ */
  placeholder?: string;

  /** The size of the number field. */
  size?: 'sm' | 'md' | 'lg';

  /** The step of the number field. This only applies when the user interacts with the input using the up/down arrows.  */
  step?: number;

  /** The value's format. (default: number) */
  valueFormat?: 'integer' | 'number';
}

export const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  function NumberInput(
    {
      'aria-label': ariaLabel,
      disabled = false,
      helpText,
      intent = 'secondary',
      invalid,
      label = '',
      max,
      min,
      onBlur,
      onChange: _onChange,
      onKeyDown,
      placeholder,
      required,
      size = 'md',
      step,
      value = '',
      valueFormat = 'number',
    },
    ref,
  ) {
    if (!intent) {
      throw new Error('"intent" prop must be supplied.');
    }
    if (!ariaLabel && !label) {
      throw new Error('Either "aria-label" prop or "label" prop must be supplied.');
    }

    const theme = useFullTheme();
    const id = useId();
    const adjustedIntent = invalid ? 'negative' : intent;
    const helpTextId = `${id}-helptext`;
    const isIntegerOnly = valueFormat === 'integer';

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      let result = value;
      if (isIntegerOnly) {
        result = value.replace(/[^0-9]/g, '');
      }
      _onChange(result);
    };

    return (
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing[2],
        }}
      >
        {label && (
          <label
            // legacy.min.css has several CSS rules that target the label tag
            css={{ margin: 0 }}
            htmlFor={id}
          >
            <LabelText disabled={disabled} required={required}>
              {label}
            </LabelText>
          </label>
        )}

        <InternalTextInput
          aria-describedby={helpText ? helpTextId : undefined}
          aria-label={label ? undefined : ariaLabel}
          disabled={disabled}
          id={id}
          intent={intent}
          invalid={invalid}
          max={max}
          min={min}
          onBlur={onBlur}
          onChange={onChange}
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
            if (isIntegerOnly && e.key === '.') {
              e.preventDefault();
            }
            onKeyDown?.(e.key);
          }}
          placeholder={placeholder}
          ref={ref}
          required={required}
          size={size}
          step={step}
          type={valueFormat === 'integer' ? 'integer' : 'number'}
          value={value}
        />

        {helpText && (
          <HelpText
            id={helpTextId}
            intent={adjustedIntent}
            showIcon={invalid || disabled || intent !== 'secondary'}
          >
            {helpText}
          </HelpText>
        )}
      </div>
    );
  },
);
